import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from './api';
import type {
  Automation,
  AutomationRun,
  CreateAutomationInput,
  DealSnapshot,
  SandboxResponse,
  UpdateAutomationInput,
} from '@server-types/automations';

/**
 * Store frontend de automações — **backend-first (commit 6, 2026-05-18)**.
 *
 * Consome os 8 endpoints REST do `server/routes/automations.ts` via React
 * Query. Tipos importados de `@server-types/automations` (single source of
 * truth — decisão 2 do Cowork).
 *
 * Hooks expostos:
 *   - useAutomations(workflowSlug)        — lista (queryKey + invalidate)
 *   - useAutomation(id)                    — single
 *   - useAutomationRuns(id, limit?)        — histórico
 *   - useAutomationActions(workflowSlug):
 *       create, update, toggle, remove, sandbox
 *
 * Padrão otimista nos toggles/updates (igual aos refactors do commit 3-4):
 *   - setQueryData mete o novo valor no cache imediato
 *   - api call em background
 *   - falha → invalidate refetcha do backend
 *
 * Re-exporta tipos do backend pra UI não precisar importar de @server-types.
 */

export type {
  Automation,
  AutomationAction,
  AutomationConditions,
  AutomationConditionRule,
  AutomationEvent,
  AutomationRun,
  AutomationTrigger,
  CreateAutomationInput,
  DealSnapshot,
  DispatchEventResult,
  DispatchResponse,
  SandboxResolvedAction,
  SandboxResponse,
  TriggerConfig,
  TriggerType,
  UpdateAutomationInput,
} from '@server-types/automations';

const KEYS = {
  list: (workflowSlug: string) => ['v1', 'automations', 'list', workflowSlug] as const,
  single: (id: string) => ['v1', 'automations', 'single', id] as const,
  runs: (id: string, limit: number) =>
    ['v1', 'automations', 'runs', id, limit] as const,
  // FEAT-V2-030 (2026-05-24): admin global — agrupa por filtros opcionais.
  adminAll: (filters: { triggerType?: string; ativo?: boolean }) =>
    ['v1', 'automations', 'admin-all', filters] as const,
  // FEAT-V2-030 (2026-05-25): métricas agregadas pra admin dashboard.
  metrics: (range: string) => ['v1', 'automations', 'metrics', range] as const,
};

// ─── Métricas (FEAT-V2-030) ─────────────────────────────────────────────

export interface AutomationMetricsTimelineEntry {
  date: string; // YYYY-MM-DD
  success: number;
  error: number;
  partial: number;
  skipped: number;
}

export interface AutomationMetrics {
  range: string;
  cutoffIso: string;
  kpis: {
    total: number;
    success: number;
    error: number;
    partial: number;
    skipped: number;
  };
  timeline: AutomationMetricsTimelineEntry[];
  /** 7 (dom..sab) × 24 (0..23h). heatmap[dow][hour] = count. */
  heatmap: number[][];
}

export function useAutomationMetrics(range: '7d' | '30d' | '90d' = '30d') {
  return useQuery({
    queryKey: KEYS.metrics(range),
    queryFn: () =>
      api.get<AutomationMetrics>(`/api/v1/automations/metrics?range=${range}`),
    enabled: hasToken(),
    staleTime: 60_000, // 1min — métricas não precisam ser real-time
  });
}

// ─── Queries ───────────────────────────────────────────────────────────

export function useAutomations(workflowSlug: string): Automation[] {
  const { data } = useQuery({
    queryKey: KEYS.list(workflowSlug),
    queryFn: () =>
      api.get<Automation[]>(`/api/v1/automations?workflow=${workflowSlug}`),
    enabled: hasToken() && !!workflowSlug,
    staleTime: 30_000,
  });
  return data ?? [];
}

/**
 * FEAT-V2-030 (2026-05-24): hook admin pra /admin/automacoes — lista
 * TODAS as automations across workflows. Backend exige is_admin (401/403
 * pra non-admin sem ?workflow=).
 *
 * Filtros opcionais aplicados na query string. queryKey memo'd via
 * KEYS.adminAll(filters).
 */
export interface AdminAutomationsFilters {
  triggerType?: string;
  ativo?: boolean;
}

export function useAdminAllAutomations(filters: AdminAutomationsFilters = {}) {
  const params = new URLSearchParams();
  if (filters.triggerType) params.set('triggerType', filters.triggerType);
  if (filters.ativo !== undefined) params.set('ativo', filters.ativo ? '1' : '0');
  const qs = params.toString();
  const url = `/api/v1/automations${qs ? `?${qs}` : ''}`;
  return useQuery({
    queryKey: KEYS.adminAll(filters),
    queryFn: () => api.get<Automation[]>(url),
    enabled: hasToken(),
    staleTime: 30_000,
  });
}

export function useAutomation(id: string | null | undefined): Automation | undefined {
  const { data } = useQuery({
    queryKey: KEYS.single(id ?? '__none__'),
    queryFn: () => api.get<Automation>(`/api/v1/automations/${id}`),
    enabled: hasToken() && !!id,
    staleTime: 30_000,
  });
  return data;
}

export function useAutomationRuns(
  id: string | null | undefined,
  limit = 50
): AutomationRun[] {
  const { data } = useQuery({
    queryKey: KEYS.runs(id ?? '__none__', limit),
    queryFn: () =>
      api.get<AutomationRun[]>(`/api/v1/automations/${id}/runs?limit=${limit}`),
    enabled: hasToken() && !!id,
    staleTime: 30_000,
  });
  return data ?? [];
}

// ─── Actions ───────────────────────────────────────────────────────────

export function useAutomationActions(workflowSlug: string) {
  const qc = useQueryClient();

  const invalidateList = () =>
    qc.invalidateQueries({ queryKey: KEYS.list(workflowSlug) });

  const create = useCallback(
    async (input: Omit<CreateAutomationInput, 'workflowSlug'>): Promise<Automation> => {
      const row = await api.post<Automation>('/api/v1/automations', {
        ...input,
        workflowSlug,
      });
      await invalidateList();
      return row;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc, workflowSlug]
  );

  const update = useCallback(
    async (id: string, patch: UpdateAutomationInput): Promise<Automation> => {
      // Otimismo no cache da lista
      qc.setQueryData<Automation[]>(KEYS.list(workflowSlug), (prev) =>
        prev
          ? prev.map((a) =>
              a.id === id ? ({ ...a, ...patch } as Automation) : a
            )
          : prev
      );
      try {
        const row = await api.put<Automation>(`/api/v1/automations/${id}`, patch);
        await invalidateList();
        return row;
      } catch (err) {
        await invalidateList(); // rollback
        throw err;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc, workflowSlug]
  );

  const toggle = useCallback(
    async (id: string, currentEnabled: boolean): Promise<void> => {
      await update(id, { enabled: !currentEnabled });
    },
    [update]
  );

  const remove = useCallback(
    async (id: string): Promise<void> => {
      qc.setQueryData<Automation[]>(KEYS.list(workflowSlug), (prev) =>
        prev ? prev.filter((a) => a.id !== id) : prev
      );
      try {
        await api.delete(`/api/v1/automations/${id}`);
      } catch (err) {
        await invalidateList();
        throw err;
      }
      await invalidateList();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc, workflowSlug]
  );

  const sandbox = useCallback(
    async (id: string, dealSnapshot: DealSnapshot): Promise<SandboxResponse> => {
      return api.post<SandboxResponse>(`/api/v1/automations/${id}/sandbox`, {
        dealSnapshot,
      });
    },
    []
  );

  return { create, update, toggle, remove, sandbox };
}

// Fase E3 (exclusão do comercial v1): `dispatchAutomations()` (fire-and-forget
// pro `POST /:workflow/dispatch`) saiu junto com a rota — o dispatch acontece
// dentro do backend desde a R3.5 e nenhum caller sobrava no frontend.
